import { useState, useEffect, useCallback, useContext, ReactNode } from "react";
import { X, Loader2, ClipboardList, Plus, ClipboardCheck, History, AlertTriangle, Eye, Pencil, TriangleAlert, CalendarPlus, CalendarDays } from "lucide-react";
import {
  fetchAttendanceRecords, fetchAttendanceEmployees, AttendanceDaySummary, AttendanceEmployee, AttendanceStats, ENTRY_STATUS_FILTERS,
} from "./api/attendanceApi";
import AppSelect from "shared/AppSelect";
import { VerifyContext } from "app/VerifyContext";
import AttendanceActionModal from "./ui/AttendanceActionModal";
import AttendanceDayDetailModal from "./ui/AttendanceDayDetailModal";
import UpdateRequestsPanel from "./ui/UpdateRequestsPanel";
import AuditLogPanel from "./ui/AuditLogPanel";
import EmployeeMultiSelect from "./ui/EmployeeMultiSelect";
import Tooltip from "shared/Tooltip";
import LeaveRequestModal from "./ui/LeaveRequestModal";
import LeaveRequestsPanel from "./ui/LeaveRequestsPanel";
import { LEAVE_TYPE_LABELS } from "./api/leaveApi";

const inputStyles =
  "px-3 py-2 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all";

const toolbarButtonStyles =
  "flex items-center gap-1.5 px-3 py-2 bg-surface-muted hover:bg-accent text-foreground text-sm font-medium font-inter rounded-xl transition-colors";

const today = new Date().toISOString().split("T")[0];

type Mode = "admin" | "hrEditor" | "hrViewer" | "selfViewer";

function formatDuration(minutes: number | null) {
  if (minutes === null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

const EMPTY_STATS: AttendanceStats = {
  total_days: 0, complete: 0, missing_check_out: 0, missing_check_in: 0, absent: 0, late: 0, leave: 0,
  on_time_rate: null, avg_arrival_time_label: null, avg_duration_minutes: null, streak_type: null, streak_count: 0,
};

function StatCard({ label, value, valueClassName }: { label: string; value: ReactNode; valueClassName?: string }) {
  return (
    <div className="bg-surface border border-border rounded-2xl px-4 py-3 flex-1 min-w-[130px]">
      <p className="text-xs text-muted-foreground font-inter">{label}</p>
      <p className={`text-xl font-semibold font-inter mt-0.5 ${valueClassName || "text-foreground"}`}>{value}</p>
    </div>
  );
}

const AttendancePage = () => {
  const { superAdmin, effectivePermissions } = useContext(VerifyContext);
  const attendancePerm = effectivePermissions["attendance"];
  const mode: Mode = superAdmin
    ? "admin"
    : attendancePerm?.can_edit
    ? "hrEditor"
    : attendancePerm?.can_view
    ? "hrViewer"
    : "selfViewer";
  const canViewAll = mode !== "selfViewer";
  const canWrite = mode === "admin" || mode === "hrEditor";

  const [days, setDays] = useState<AttendanceDaySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1); // updated from API response
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [truncated, setTruncated] = useState(false);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [employeeCodes, setEmployeeCodes] = useState<string[]>([]);
  const [entryStatus, setEntryStatus] = useState("");
  const [lateOnly, setLateOnly] = useState(false);
  const [stats, setStats] = useState<AttendanceStats>(EMPTY_STATS);
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: today, endDate: today, employeeCodes: [] as string[], entryStatus: "", lateOnly: false,
  });

  // undefined (not []) until the first fetch actually resolves, so children
  // relying on this via the employees prop can tell "not loaded yet" apart
  // from "org genuinely has zero employees" — see the fetch effect below.
  const [employees, setEmployees] = useState<AttendanceEmployee[] | undefined>(undefined);
  const [detailDay, setDetailDay] = useState<AttendanceDaySummary | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showMyLeaveRequests, setShowMyLeaveRequests] = useState(false);

  const PAGE_SIZE = 50;

  const load = useCallback(async (p: number, filters: typeof appliedFilters) => {
    setLoading(true);
    try {
      const data = await fetchAttendanceRecords(
        p, PAGE_SIZE,
        filters.startDate || undefined,
        filters.endDate || undefined,
        filters.employeeCodes,
        filters.entryStatus || undefined,
        filters.lateOnly,
      );
      setDays(data.items || []);
      setPage(data.page || 1);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count || 0);
      setTruncated(!!data.truncated);
      setStats(data.stats || EMPTY_STATS);
    } catch (err) {
      console.error("Failed to load attendance records:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(1, appliedFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canViewAll) return;
    // On failure, deliberately leave `employees` as whatever it already was
    // (undefined if this is the first attempt) rather than resetting to []
    // — an empty array here is indistinguishable from "still loading" to
    // AttendanceActionModal/LeaveRequestModal's own employeesProp fallback
    // logic, which would otherwise wipe out a list either of them had
    // already self-fetched successfully.
    fetchAttendanceEmployees().then(setEmployees).catch((err) => console.error("Failed to load employees:", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAll]);

  const handleApplyFilters = () => {
    const filters = { startDate, endDate, employeeCodes, entryStatus, lateOnly };
    setAppliedFilters(filters);
    load(1, filters);
  };

  const handleClearFilters = () => {
    setStartDate(today);
    setEndDate(today);
    setEmployeeCodes([]);
    setEntryStatus("");
    setLateOnly(false);
    const reset = { startDate: today, endDate: today, employeeCodes: [] as string[], entryStatus: "", lateOnly: false };
    setAppliedFilters(reset);
    load(1, reset);
  };

  const toggleLateOnly = () => {
    const next = !lateOnly;
    setLateOnly(next);
    const filters = { startDate, endDate, employeeCodes, entryStatus, lateOnly: next };
    setAppliedFilters(filters);
    load(1, filters);
  };

  const hasFilters = startDate !== today || endDate !== today || employeeCodes.length > 0 || entryStatus || lateOnly;

  const formatTime = (iso: string) => {
    // Python isoformat() returns +00:00 offset, not Z — new Date() handles both formats
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  };

  const thClass = "py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter";
  const tdClass = "py-3.5 px-4 text-sm text-foreground font-inter";

  const writeMode: "admin-direct" | "hr-request" = mode === "admin" ? "admin-direct" : "hr-request";
  const detailWriteMode: "admin-direct" | "hr-request" | "readonly" = canWrite ? writeMode : "readonly";
  const refresh = () => load(page, appliedFilters);

  function renderPunchCell(
    punch: AttendanceDaySummary["check_in"],
    options: { hasMultiple: boolean; isAbsent: boolean; lateByMinutes?: number | null; isLeave?: boolean; leaveType?: string | null },
  ) {
    const { hasMultiple, isAbsent, lateByMinutes, isLeave, leaveType } = options;
    if (!punch) {
      if (isLeave) {
        return (
          <Tooltip content={leaveType ? `Approved ${LEAVE_TYPE_LABELS[leaveType] || leaveType}` : "Approved leave"}>
            <span className="text-violet-500 font-medium">On Leave</span>
          </Tooltip>
        );
      }
      return isAbsent
        ? <span className="text-destructive font-medium">Absent</span>
        : <span className="text-muted-foreground">— Missing</span>;
    }
    return (
      <span className="inline-flex items-center gap-1.5">
        {formatTime(punch.timestamp)}
        {!!lateByMinutes && (
          <Tooltip content={`First arrival was ${lateByMinutes} minutes after the 10:00 AM PKT shift start (30-minute grace period)${hasMultiple ? " — this day has multiple check-ins; the time shown here is the last one, not the late arrival" : ""}`}>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium font-inter bg-amber-500/15 text-amber-500">
              First arrival: Late {lateByMinutes}m
            </span>
          </Tooltip>
        )}
        {punch.is_manually_modified && (
          <Tooltip asLabel content={`${punch.modified_action === "add" ? "Added" : punch.modified_action === "delete" ? "Removed" : "Edited"} by ${punch.modified_by}${punch.modified_at ? ` on ${new Date(punch.modified_at).toLocaleString()}` : ""}`}>
            <History className="w-3 h-3 text-amber-400" />
          </Tooltip>
        )}
        {hasMultiple && (
          <Tooltip asLabel content="Multiple entries this day — open details to view all">
            <TriangleAlert className="w-3 h-3 text-orange-400" />
          </Tooltip>
        )}
      </span>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-inter">Attendance</h1>
          <p className="text-sm text-muted-foreground font-inter mt-1">
            {totalCount > 0 ? `${totalCount.toLocaleString()} days` : "Daily check-in/check-out summary from biometric devices"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {mode === "admin" && (
            <button onClick={() => setShowAuditLog(true)} className={toolbarButtonStyles}>
              <History className="w-4 h-4" /> Audit Log
            </button>
          )}
          {/* Admin sees every request (review/approve/reject + its own "Add
              Leave" entry point inside the panel); any employee session
              sees only their own — requesting your own leave needs no
              special permission (unlike attendance corrections, gated on
              canWrite). */}
          <button onClick={() => setShowMyLeaveRequests(true)} className={toolbarButtonStyles}>
            <CalendarDays className="w-4 h-4" /> {mode === "admin" ? "Leave Requests" : "My Leave Requests"}
          </button>
          {mode !== "admin" && (
            <button
              onClick={() => setShowLeaveModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              <CalendarPlus className="w-4 h-4" /> Request Leave
            </button>
          )}
          {canWrite && (
            <button onClick={() => setShowRequests(true)} className={toolbarButtonStyles}>
              <ClipboardCheck className="w-4 h-4" /> {mode === "admin" ? "Update Requests" : "My Requests"}
            </button>
          )}
          {canWrite && (
            <Tooltip content="For an employee/day with no existing punches at all — otherwise open a row and use Add Missing Check-In/Out" align="right">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" /> {mode === "admin" ? "Add Record" : "Request Missing Punch"}
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Stats — summarizes the current date range + employee selection (see
          AttendanceStats docstring); stays fixed while the Status/Late chip
          drills the table down, rather than shifting with it. Defaults to
          today org-wide on load; narrowing to one employee (or a self-view)
          adds the on-time-rate/arrival/duration/streak cards. */}
      <div className="flex flex-wrap gap-3 mb-5">
        <StatCard label="Total Days" value={stats.total_days} />
        <StatCard label="Complete" value={stats.complete} valueClassName="text-emerald-500" />
        <StatCard label="Late" value={stats.late} valueClassName="text-amber-500" />
        <StatCard label="Absent" value={stats.absent} valueClassName="text-destructive" />
        <StatCard label="Incomplete" value={stats.missing_check_in + stats.missing_check_out} />
        {/* stats.on_time_rate !== null doubles as "is this scoped to one
            employee" — the same signal the backend uses to decide whether
            to populate leave/on_time_rate/streak at all (stats.leave is 0 in
            both the unscoped case AND a scoped employee who's taken none, so
            it can't tell those apart on its own). */}
        {stats.on_time_rate !== null && (
          <StatCard
            label="On-Time Rate"
            value={`${stats.on_time_rate}%`}
            valueClassName={stats.on_time_rate >= 90 ? "text-emerald-500" : stats.on_time_rate >= 70 ? "text-amber-500" : "text-destructive"}
          />
        )}
        {stats.on_time_rate !== null && <StatCard label="On Leave" value={stats.leave} valueClassName="text-violet-500" />}
        {stats.avg_arrival_time_label && <StatCard label="Avg. Arrival" value={stats.avg_arrival_time_label} />}
        {stats.avg_duration_minutes !== null && <StatCard label="Avg. Duration" value={formatDuration(stats.avg_duration_minutes)} />}
        {stats.streak_type && (
          <StatCard
            label="Current Streak"
            value={`${stats.streak_count} ${stats.streak_type === "late" ? "Late" : "On-Time"}`}
            valueClassName={stats.streak_type === "late" ? "text-destructive" : "text-emerald-500"}
          />
        )}
      </div>

      {/* Filters */}
      <div className="bg-surface border border-border rounded-2xl p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-inter">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputStyles}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-inter">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputStyles}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-inter">Status</label>
            <AppSelect value={entryStatus} onChange={setEntryStatus}>
              {ENTRY_STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </AppSelect>
          </div>
          {canViewAll && (
            <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
              <label className="text-xs text-muted-foreground font-inter">Employees</label>
              <EmployeeMultiSelect employees={employees ?? []} selectedCodes={employeeCodes} onChange={setEmployeeCodes} />
            </div>
          )}
          <Tooltip content="Check-in more than 30 minutes after the 10:00 AM PKT shift start">
            <button
              onClick={toggleLateOnly}
              className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl border text-xs font-medium font-inter transition-colors ${
                lateOnly
                  ? "bg-amber-500/15 text-amber-500 border-amber-500/40"
                  : "bg-surface-muted/60 text-muted-foreground border-border hover:text-foreground"
              }`}
            >
              <TriangleAlert className="w-3.5 h-3.5" /> Late Arrivals
              {stats.late > 0 && (
                <span className={`text-[10px] font-semibold px-1 py-0.5 rounded min-w-[16px] text-center leading-none ${
                  lateOnly ? "bg-amber-500/25 text-amber-500" : "bg-muted text-muted-foreground"
                }`}>
                  {stats.late}
                </span>
              )}
            </button>
          </Tooltip>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
          >
            Apply
          </button>
          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-muted-foreground hover:text-foreground text-sm font-inter rounded-xl hover:bg-surface-muted transition-colors flex items-center gap-1.5"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {truncated && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-sm text-amber-500 font-inter">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Showing partial results — narrow the date range or employee filter for a complete view.
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : days.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-12 text-center">
          <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground font-inter text-sm">No attendance records found.</p>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[750px]">
              <thead className="bg-surface-muted">
                <tr className="border-b border-border">
                  <th className={thClass}>Date</th>
                  <th className={thClass}>Employee</th>
                  <th className={thClass}>Check-In</th>
                  <th className={thClass}>Check-Out</th>
                  <th className={thClass}>Duration</th>
                  <th className={thClass}></th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const isAbsent = d.completeness === "absent";
                  return (
                  <tr
                    key={`${d.employee_code}-${d.date}`}
                    className={`border-b border-border last:border-0 hover:bg-surface-muted transition-colors ${
                      d.is_weekend
                        ? "bg-muted/40 opacity-70"
                        : d.is_leave
                        ? "bg-violet-500/5"
                        : isAbsent
                        ? "bg-destructive/5"
                        : d.is_late
                        ? "bg-amber-500/5"
                        : ""
                    }`}
                  >
                    <td className={tdClass}>
                      <span className="inline-flex items-center gap-1.5">
                        {formatDate(d.date)}
                        {d.is_weekend && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium font-inter bg-sky-500/10 text-sky-400">
                            Weekend
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <span className={d.employee_name === "—" ? "text-muted-foreground" : "text-foreground"}>
                        {d.employee_name}
                      </span>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{d.employee_code}</div>
                    </td>
                    <td className={`${tdClass} font-mono`}>
                      {renderPunchCell(d.check_in, {
                        hasMultiple: d.has_multiple_check_in, isAbsent, lateByMinutes: d.late_by_minutes,
                        isLeave: d.is_leave, leaveType: d.leave_type,
                      })}
                    </td>
                    <td className={`${tdClass} font-mono`}>
                      {renderPunchCell(d.check_out, {
                        hasMultiple: d.has_multiple_check_out, isAbsent, isLeave: d.is_leave, leaveType: d.leave_type,
                      })}
                    </td>
                    <td className={`${tdClass} text-muted-foreground`}>{formatDuration(d.duration_minutes)}</td>
                    <td className={tdClass}>
                      <Tooltip asLabel content={canWrite ? "View / manage" : "View details"} align="right">
                        <button
                          onClick={() => setDetailDay(d)}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface rounded-lg transition-colors"
                        >
                          {canWrite ? <Pencil className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground font-inter">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => load(page - 1, appliedFilters)}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-xs font-inter text-muted-foreground hover:text-foreground hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => load(page + 1, appliedFilters)}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-xs font-inter text-muted-foreground hover:text-foreground hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {detailDay && (
        <AttendanceDayDetailModal
          day={detailDay}
          writeMode={detailWriteMode}
          onClose={() => setDetailDay(null)}
          onSuccess={refresh}
        />
      )}
      {showAddModal && (
        <AttendanceActionModal
          writeMode={writeMode}
          action="add"
          employees={employees}
          onClose={() => setShowAddModal(false)}
          onSuccess={refresh}
        />
      )}
      {showRequests && (
        <UpdateRequestsPanel
          role={mode === "admin" ? "admin" : "hr"}
          onClose={() => setShowRequests(false)}
          onReviewed={refresh}
        />
      )}
      {showAuditLog && <AuditLogPanel onClose={() => setShowAuditLog(false)} />}
      {showLeaveModal && (
        <LeaveRequestModal writeMode="self-request" onClose={() => setShowLeaveModal(false)} onSuccess={refresh} />
      )}
      {showMyLeaveRequests && (
        <LeaveRequestsPanel
          role={mode === "admin" ? "admin" : "employee"}
          employees={mode === "admin" ? employees : undefined}
          onClose={() => setShowMyLeaveRequests(false)}
          onSuccess={refresh}
        />
      )}
    </>
  );
};

export default AttendancePage;
